import {
  AspectRatio,
  Flex,
  HStack,
  Link,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage } from "react-intl";
import { Icon } from "../../../components/Icon";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 */
/**
 * @param {Props} props
 */
export function DetailHeaderRentalArea(props) {
  const { rentalArea } = props;

  const translate = useTranslate();

  const format =
    useBreakpointValue({ base: "default", lg: "large" }) ?? "default";

  return (
    <Flex
      my="1rem"
      p=".75rem"
      direction={{ base: "column", md: "row" }}
      bg="gray.50"
      gap="1rem"
      borderRadius="4px">
      <AspectRatio ratio={16 / 9} w="120px" h="70px">
        <PampaImage
          image={rentalArea?.covers?.[0]}
          alt={translate(rentalArea?.covers?.[0]?.caption) ?? ""}
          format={format}
          borderRadius="4px"
          mode="crop"
          placeholderSrc={generateFrontPath({
            id: "@front.internal-assets",
            parameters: { filePath: "placeholder.svg" },
            includeBasePath: true,
          })}
          _groupHover={{
            transform: "scale(1.1)",
          }}
          transition="all 0.25s ease"
          bg="gray.100"
        />
      </AspectRatio>

      <VStack align="start">
        <Text variant="h3" fontWeight={400}>
          {translate(rentalArea.name)}
        </Text>

        <HStack>
          <Icon icon="ms_place" size="1.25rem" color="gray.400" />

          <Text color="gray.600" textDecoration="underline">
            <FormattedMessage
              defaultMessage="{address}"
              values={{
                address: rentalArea?.address2
                  ? `${rentalArea.address1}, ${rentalArea.address2}`
                  : rentalArea.address1,
              }}
            />
          </Text>
        </HStack>
      </VStack>

      <VStack
        align="start"
        spacing="0"
        gap="1rem"
        display={{ base: "none", sm: "flex" }}>
        <HStack align="start" spacing="0" gap="1rem" wrap="wrap">
          {rentalArea.opening_hour && rentalArea.closing_hour && (
            <HStack>
              <Icon icon="ms_schedule" size="1.25rem" color="gray.400" />

              <Text color="gray.600">
                <FormattedMessage
                  defaultMessage="De {opening} à {closing}"
                  values={{
                    opening: rentalArea.opening_hour?.slice(0, -3),
                    closing: rentalArea.closing_hour?.slice(0, -3),
                  }}
                />
              </Text>
            </HStack>
          )}

          <HStack>
            <Icon icon="ms_group" size="1.25rem" color="gray.400" />

            <Text color="gray.600">
              <FormattedMessage
                defaultMessage="{capacity} personnes maximum"
                values={{ capacity: rentalArea.max_capacity }}
              />
            </Text>
          </HStack>

          <HStack>
            <Icon icon="ms_fullscreen" size="1.25rem" color="gray.400" />

            <Text color="gray.600">
              {rentalArea.surface ? (
                <FormattedMessage
                  defaultMessage="{surface} m²"
                  values={{ surface: rentalArea.surface }}
                />
              ) : (
                <FormattedMessage defaultMessage="Surface non renseignée" />
              )}
            </Text>
          </HStack>
        </HStack>

        <HStack align="start" spacing="0" gap="1rem" wrap="wrap">
          {rentalArea.phone_country && rentalArea.phone_number && (
            <HStack align="start">
              <Icon icon="ms_phone" size="1.25rem" color="gray.400" />

              <Text color="gray.600" wordBreak="break-word" noOfLines={5}>
                <Link href={`tel:${rentalArea.phone_number}`}>
                  {rentalArea.phone_number
                    .replace(/^\+33/, "0")
                    .replace(/(\d{2})(?=\d)/g, "$1 ")}
                </Link>
              </Text>
            </HStack>
          )}

          {rentalArea.website && (
            <HStack align="start">
              <Icon icon="ms_info" size="1.25rem" color="gray.400" />

              <Text color="gray.600">
                <Link href={`https://${rentalArea.website}`} isExternal>
                  {rentalArea.website}
                </Link>
              </Text>
            </HStack>
          )}
        </HStack>
      </VStack>
    </Flex>
  );
}
